import { useFormContext, useWatch } from 'react-hook-form';

import { Grid } from '@mui/material';

import { useGlobalProducts } from 'components/BusinessToolForm/hooks/useGlobalProducts';
import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import {
  REVENUE_PLAN_NAMES,
  prefix as revenuePrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/RevenuePlan/utils/form';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';
import { PRODUCT_NAMES } from 'components/BusinessToolForm/Sections/Global/general/products';

import { concatWithDot } from 'helpers/string';

import { getEachRowValues } from './helpers';
import { NET_SALES_NAMES, netSalesLinkedFieldNames, useGetNetSalesTableHeadings } from './utils/form';
import { FIRST_COLUMN_WIDTH, netSalesPrefix } from '../../../utils/form';

export const NetSalesForm: React.FC<{ disabled: boolean }> = ({ disabled = false }) => {
  const period = useWatch({ name: GLOBAL_PERIOD_NAME });
  const { OTHER_INCOME, totalRowTitle, costOfSalesRowTitle, netSalesRowTitle } = useGetNetSalesTableHeadings();

  const { getValues } = useFormContext();

  const { revenuePlanFields } = useGlobalProducts();

  const revenuePlanRowTitles = getValues(netSalesLinkedFieldNames.revenuePlanRowTitleName).map(
    (value: Record<string, string>) => value[PRODUCT_NAMES.NAME] ?? 'Untitled',
  );

  const otherIncomeValue = getValues(netSalesLinkedFieldNames.otherIncomeName);
  const costOfSalesValues = getValues(concatWithDot(netSalesPrefix, NET_SALES_NAMES.COST_OF_SALES));
  const totalGrossProfit = getValues(concatWithDot(netSalesPrefix, NET_SALES_NAMES.GROSS_PROFIT));

  const revenuePlanTotalValues = getValues(
    concatWithDot(revenuePrefix, REVENUE_PLAN_NAMES.PLANNED_PRODUCTS_VOLUME_TOTAL),
  );

  return (
    <Grid container gap={5} direction={'column'}>
      <Grid item xs>
        <Grid item xs>
          <TotalRowComponent
            name={concatWithDot(netSalesPrefix, NET_SALES_NAMES.NET_SALES)}
            columnValues={getEachRowValues([revenuePlanTotalValues], 0, period)}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            totalRowTitle={netSalesRowTitle}
            noNeedName={true}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          {revenuePlanRowTitles.map((title: string, index: number) => {
            return (
              <Grid item xs key={index}>
                <ReadOnlyTableRow
                  name={concatWithDot(netSalesPrefix, NET_SALES_NAMES.INCOME)}
                  columnValues={getEachRowValues(revenuePlanFields, index, period)}
                  firstColumnWidth={FIRST_COLUMN_WIDTH}
                  rowTitle={title}
                  noNeedName={true}
                  disabled={disabled}
                />
              </Grid>
            );
          })}
          <Grid item xs>
            <ReadOnlyTableRow
              name={concatWithDot(netSalesPrefix, NET_SALES_NAMES.INCOME)}
              rowIndex={revenuePlanFields.length}
              columnValues={getEachRowValues(otherIncomeValue, 0, period)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              rowTitle={OTHER_INCOME}
              noNeedName={true}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <Grid item xs>
          <ReadOnlyTableRow
            name={concatWithDot(netSalesPrefix, NET_SALES_NAMES.COST_OF_SALES)}
            columnValues={getEachRowValues([costOfSalesValues], 0, period)}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            rowTitle={costOfSalesRowTitle}
            noNeedName={true}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          <TotalRowComponent
            name={concatWithDot(netSalesPrefix, NET_SALES_NAMES.GROSS_PROFIT)}
            columnValues={getEachRowValues([totalGrossProfit], 0, period)}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            totalRowTitle={totalRowTitle}
            noNeedName={true}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
